import { Button, makeStyles, mergeClasses, shorthands, tokens, Text } from '@fluentui/react-components';
import { Navigation24Regular, Dismiss24Regular } from '@fluentui/react-icons';
import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// App owner Client ID for Configuration access
const APP_OWNER_ID = 'df30bdbc-a977-4a2b-a065-2add4f4605cf';

const useStyles = makeStyles({
  loginContainer: {
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalXL
  },
  loginTitle: {
    fontSize: tokens.fontSizeHero900,
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorNeutralForeground1
  },
  container: {
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    display: 'grid',
    gridTemplateColumns: '16rem 1fr',
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr',
      gridTemplateRows: 'auto 1fr'
    }
  },
  mobileHeader: {
    display: 'none',
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    ...shorthands.padding('1rem'),
    justifyContent: 'space-between',
    alignItems: 'center',
    '@media (max-width: 768px)': {
      display: 'flex'
    }
  },
  mobileTitle: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold
  },
  hamburgerButton: {
    color: 'inherit',
    minWidth: 'auto',
    padding: tokens.spacingHorizontalS
  },
  nav: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    ...shorthands.padding('2rem', '1.5rem'),
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    '@media (max-width: 768px)': {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 1000,
      paddingTop: '4rem',
      transform: 'translateX(-100%)',
      transition: 'transform 0.3s ease-in-out'
    }
  },
  navOpen: {
    '@media (max-width: 768px)': {
      transform: 'translateX(0)'
    }
  },
  navCloseButton: {
    display: 'none',
    position: 'absolute',
    top: '1rem',
    right: '1rem',
    color: 'inherit',
    '@media (max-width: 768px)': {
      display: 'block'
    }
  },
  navHeader: {
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightSemibold,
    marginBlockEnd: '2rem',
    '@media (max-width: 768px)': {
      display: 'none'
    }
  },
  navLink: {
    color: 'inherit',
    textDecoration: 'none',
    fontSize: tokens.fontSizeBase400,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusMedium,
    transition: 'background-color 0.2s',
    ':hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)'
    },
    '&.active': {
      fontWeight: tokens.fontWeightSemibold,
      backgroundColor: 'rgba(255, 255, 255, 0.2)'
    }
  },
  navDivider: {
    height: '1px',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    margin: `${tokens.spacingVerticalM} 0`
  },
  content: {
    ...shorthands.padding('2rem'),
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
    overflowY: 'auto',
    '@media (max-width: 768px)': {
      ...shorthands.padding('1rem')
    }
  },
  footer: {
    marginTop: 'auto',
    fontSize: tokens.fontSizeBase200,
    opacity: 0.7
  },
  overlay: {
    display: 'none',
    '@media (max-width: 768px)': {
      display: 'block',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 999
    }
  }
});

const LayoutShell: React.FC<React.PropsWithChildren> = ({ children }) => {
  const styles = useStyles();
  const { principal, isLoading, error } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Close menu when window resizes to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check if user is app owner (for Configuration access)
  const isAppOwner = principal?.userId === APP_OWNER_ID || 
    principal?.userRoles?.includes('app-owner') ||
    principal?.userRoles?.includes('group_admin');

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className={styles.loginContainer}>
        <Text className={styles.loginTitle}>Loading...</Text>
      </div>
    );
  }

  // Show error if auth failed
  if (error) {
    return (
      <div className={styles.loginContainer}>
        <Text className={styles.loginTitle}>Authentication Error</Text>
        <Text>{error}</Text>
        <Button
          appearance="primary"
          size="large"
          onClick={() => window.location.href = '/.auth/login/aad'}
        >
          Try Again
        </Button>
      </div>
    );
  }

  // Show minimal login page for unauthenticated users
  if (!principal) {
    return (
      <div className={styles.loginContainer}>
        <Text className={styles.loginTitle}>IT Tool</Text>
        <Button
          appearance="primary"
          size="large"
          onClick={() => window.location.href = '/.auth/login/aad'}
        >
          Sign in
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Mobile Header */}
      <div className={styles.mobileHeader}>
        <span className={styles.mobileTitle}>Entra Groups</span>
        <Button
          appearance="transparent"
          className={styles.hamburgerButton}
          icon={<Navigation24Regular />}
          onClick={() => setIsMenuOpen(true)}
          aria-label="Open menu"
        />
      </div>

      {/* Overlay for mobile */}
      {isMenuOpen && (
        <div
          className={styles.overlay}
          role="button"
          tabIndex={0}
          aria-label="Close menu"
          onClick={() => setIsMenuOpen(false)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setIsMenuOpen(false);
            }
          }}
        />
      )}

      {/* Navigation */}
      <nav className={mergeClasses(styles.nav, isMenuOpen && styles.navOpen)}>
        <Button
          appearance="transparent"
          className={styles.navCloseButton}
          icon={<Dismiss24Regular />}
          onClick={() => setIsMenuOpen(false)}
          aria-label="Close menu"
        />
        <span className={styles.navHeader}>Entra Groups</span>
        <NavLink to="/" className={styles.navLink} end>
          Overview
        </NavLink>
        <NavLink to="/rename" className={styles.navLink}>
          Rename Group
        </NavLink>
        <NavLink to="/review" className={styles.navLink}>
          Review Requests
        </NavLink>
        <NavLink to="/transactions" className={styles.navLink}>
          Transaction Log
        </NavLink>
        
        {isAppOwner && (
          <>
            <div className={styles.navDivider} />
            <NavLink to="/config" className={styles.navLink}>
              Configuration
            </NavLink>
          </>
        )}

        <div className={styles.footer}>
          {isLoading && <span>Loading user...</span>}
          {!isLoading && principal && (
            <>
              <span>{principal.userDetails}</span>
              <br />
              <a href="/.auth/logout" style={{ color: 'inherit' }}>Sign out</a>
            </>
          )}
          {!isLoading && !principal && (
            <a href="/.auth/login/aad" style={{ color: 'inherit', fontWeight: 'bold' }}>Sign in</a>
          )}
        </div>
      </nav>
      <main className={styles.content}>{children}</main>
    </div>
  );
};

export default LayoutShell;
